import React, { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";
import dayjs from "dayjs";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  LinearProgress,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme, lighten, darken } from "@mui/material/styles";
import {
  ArrowBack,
  AutoAwesome,
  AccessTime as AccessTimeIcon,
  Insights as InsightsIcon,
  PowerSettingsNew as PowerSettingsNewIcon,
  Send as SendIcon,
  Groups as GroupsIcon,
  SupportAgent as SupportAgentIcon,
  Tab as TabIcon,
  Timer as TimerIcon,
  Wifi as WifiIcon,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { getUserOperatorAnalytics, getDashboardMetricInsights } from "../../../api/client";
import { formatDateTimePtBR } from "../../../utils/dateTime";
import MetricAiInsightsDialog from "../../../components/MetricAiInsightsDialog";
import { ensureNamespaceLoaded } from "../../../i18n/lazyNamespaces";
import { useAuthContext } from "../../../context/AuthContext";
import { useTeamSocketPresence } from "../../../hooks/useTeamSocketPresence";
import { getTeamPresenceKind } from "../../../utils/teamPresence";

const BR = (x) => (Number.isFinite(x) ? x : Number(x ?? 0)).toLocaleString("pt-BR");

const buildSeriesFromTheme = (theme) => {
  const p = theme.palette;
  return [
    p.primary.main,
    p.secondary?.main ?? p.info.main,
    p.warning.main,
    p.success.main,
    p.error.main,
    p.info.main,
    darken(p.primary.main, 0.2),
    lighten(p.primary.main, 0.35),
  ];
};

/** Mesmo critério do dashboard (Atendimento): dias/horas/minutos compactos */
const secToHuman = (sec) => {
  const s = Math.max(0, Math.round(Number(sec || 0)));
  if (!s) return "0m";
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d${h ? ` ${h}h` : ""}`;
  if (h > 0) return `${h}h${m ? ` ${m}m` : ""}`;
  return `${m || 1}m`;
};

const fmtDurationSec = (sec) => {
  const n = Number(sec);
  if (!Number.isFinite(n) || n < 0) return "—";
  if (n < 60) return `${Math.round(n)}s`;
  const m = Math.floor(n / 60);
  const s = Math.round(n % 60);
  return `${m}min ${s}s`;
};

const hourLabel = (h) => `${String(h).padStart(2, "0")}:00`;

function buildAiPayload(analytics, period, t, operatorUserId) {
  const cards = analytics?.cards || {};
  const charts = analytics?.charts || {};
  const user = analytics?.user || {};
  const metricValue = [
    ...(Array.isArray(charts.serieTemporal) ? charts.serieTemporal : []).map((d) => ({
      day: d.day,
      value: d.value,
    })),
    ...(Array.isArray(charts.motivosTop) ? charts.motivosTop : []).map((m) => ({
      path: m.path,
      value: m.value,
    })),
    ...(Array.isArray(analytics.channels) ? analytics.channels : []).map((c) => ({
      name: `${t("userOperatorAnalytics.aiChannelPrefix")}: ${c.name}`,
      value: c.value,
    })),
    ...(Array.isArray(analytics.interactionByHour) ? analytics.interactionByHour : []).map((h) => ({
      name: `${t("userOperatorAnalytics.aiHourPrefix")} ${hourLabel(h.hour)}`,
      value: h.value,
    })),
  ];

  const summaryContext = {
    escopoAtendente: true,
    idUsuarioOperador: Number(operatorUserId) > 0 ? Number(operatorUserId) : null,
    nomeAtendente: user.nome || "",
    totalSolicitacoes: cards.totalAtendimentos ?? null,
    tmaMedioSec: cards.avgTMAsec ?? null,
    tmeMedioSec: cards.avgTMEsec ?? null,
    pessoasContatadas: analytics.operatorDistinctClients ?? null,
    mensagensEnviadasNoPeriodo: analytics.operatorMessagesTotal ?? null,
    pesquisasConcluidas: analytics.surveys?.completedSessions ?? null,
    topMotivos: (charts.motivosTop || []).slice(0, 10).map((x) => x.path),
    resumoPesquisas: (analytics.surveys?.bySurvey || []).slice(0, 8).map((s) => ({
      titulo: s.surveyTitle,
      concluidas: s.completed,
      notaMedia: s.avgScore,
    })),
  };

  const metricHuman = [
    `${t("userOperatorAnalytics.kpiTotal")}: ${BR(cards.totalAtendimentos ?? 0)}`,
    `TMA: ${secToHuman(cards.avgTMAsec)}`,
    `TME: ${secToHuman(cards.avgTMEsec)}`,
    `${t("userOperatorAnalytics.kpiPeopleContacted")}: ${BR(analytics.operatorDistinctClients ?? 0)}`,
  ].join(" · ");

  return {
    metric: "operator_performance",
    metricName: t("userOperatorAnalytics.aiMetricName"),
    metricLabel: t("userOperatorAnalytics.aiMetricLabel"),
    metricDefinition: t("userOperatorAnalytics.aiMetricDefinition"),
    metricValue,
    metricHuman,
    period,
    summaryContext,
    filters: {},
    fullSnapshot: {
      user: { nome: user.nome, cargo: user.cargoNome, isOnline: user.isOnline },
      period,
      cards,
      operatorDistinctClients: analytics.operatorDistinctClients,
      surveys: analytics.surveys,
      channels: analytics.channels,
    },
  };
}

function mergeOperatorMetricCfg(analytics, period, t, operatorUserId, partial) {
  const base = buildAiPayload(analytics, period, t, operatorUserId);
  return {
    ...base,
    ...partial,
    summaryContext: { ...base.summaryContext, ...(partial.summaryContext || {}) },
  };
}

function OperatorKpiCard({ loading, icon, title, value, color, sub, onAi, explainAi, explainAiAria, aiBusy }) {
  if (loading) {
    return (
      <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
        <Skeleton variant="rounded" height={118} sx={{ width: "100%", borderRadius: 1 }} />
      </Grid>
    );
  }
  return (
    <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
      <Card
        elevation={1}
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          border: 1,
          borderColor: "divider",
          boxShadow: (th) =>
            th.palette.mode === "dark"
              ? `0 1px 4px ${alpha("#000", 0.35)}`
              : `0 1px 3px ${alpha(th.palette.common.black, 0.08)}`,
        }}
      >
        <CardContent sx={{ flex: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: color, width: 44, height: 44 }}>{icon}</Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }} noWrap>
                  {title}
                </Typography>
                {onAi ? (
                  <Tooltip title={explainAi}>
                    <IconButton size="small" onClick={onAi} disabled={aiBusy} aria-label={explainAiAria}>
                      <AutoAwesome fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </Stack>
              <Typography variant="h4" sx={{ color, lineHeight: 1.15, fontWeight: 600 }}>
                {value}
              </Typography>
              {sub ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                  {sub}
                </Typography>
              ) : null}
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  );
}

const dashboardSectionCardSx = {
  border: 1,
  borderColor: "divider",
  boxShadow: (th) =>
    th.palette.mode === "dark"
      ? `0 1px 4px ${alpha("#000", 0.35)}`
      : `0 1px 3px ${alpha(th.palette.common.black, 0.08)}`,
};

/** Alinhado a TableContainer/Table de telas admin (ex.: WorkspacesTabPanel): outlined, cabeçalho fixo, zebra e hover */
function analyticsDataTableSx(theme) {
  const headBg = alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.14 : 0.07);
  return {
    "& .MuiTableCell-head": {
      fontWeight: 600,
      fontSize: "0.75rem",
      letterSpacing: "0.02em",
      color: theme.palette.text.secondary,
      backgroundColor: headBg,
      borderBottom: `1px solid ${theme.palette.divider}`,
      whiteSpace: "nowrap",
      py: 1.125,
    },
    "& .MuiTableCell-body": {
      fontSize: "0.8125rem",
      py: 1.125,
      borderColor: theme.palette.divider,
      verticalAlign: "middle",
    },
    "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even) .MuiTableCell-body": {
      backgroundColor: alpha(theme.palette.action.hover, theme.palette.mode === "dark" ? 0.06 : 0.04),
    },
  };
}

const UserOperatorAnalytics = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const dataTableSx = useMemo(() => analyticsDataTableSx(theme), [theme]);
  const SERIES = useMemo(() => buildSeriesFromTheme(theme), [theme]);
  const { t, i18n } = useTranslation("settings");
  const { t: tChat } = useTranslation("chat");
  const [start, setStart] = useState(() => dayjs().startOf("month"));
  const [end, setEnd] = useState(() => dayjs().endOf("month"));
  const [aiOpen, setAiOpen] = useState(false);
  const [aiCfg, setAiCfg] = useState(null);
  const [aiData, setAiData] = useState(null);
  const [aiError, setAiError] = useState(null);

  const { userData } = useAuthContext();
  const { tabsByUserId } = useTeamSocketPresence(userData);

  const startStr = start.format("YYYY-MM-DD");
  const endStr = end.format("YYYY-MM-DD");

  React.useEffect(() => {
    ensureNamespaceLoaded("chat");
  }, []);

  const { data, isLoading, error, refetch, isFetching } = useQuery(
    ["userOperatorAnalytics", id, startStr, endStr],
    () => getUserOperatorAnalytics(id, { start: startStr, end: endStr }),
    { enabled: !!id, keepPreviousData: true }
  );

  const metricInsightsMutation = useMutation(
    async (cfg) => {
      const payload = {
        metric: cfg.metric,
        metricName: cfg.metricName,
        metricDefinition: cfg.metricDefinition,
        metricValue: cfg.metricValue,
        metricHuman: cfg.metricHuman,
        period: cfg.period,
        ...(cfg.metricSec != null ? { metricSec: cfg.metricSec } : {}),
        summaryContext: {
          ...cfg.summaryContext,
          analyticsEscopoAtendente: true,
          snapshot: cfg.fullSnapshot,
        },
      };
      return getDashboardMetricInsights("atendimento", payload);
    },
    {
      onSuccess: (res) => {
        setAiData(res || null);
        setAiError(null);
      },
      onError: (err) => {
        const msg =
          (typeof err?.message === "string" && err.message) ||
          (typeof err?.error === "string" && err.error) ||
          t("userOperatorAnalytics.aiError");
        setAiError(msg);
      },
    }
  );

  const openAi = useCallback(() => {
    if (!data) return;
    const period = { start: startStr, end: endStr };
    const cfg = buildAiPayload(data, period, t, id);
    setAiCfg(cfg);
    setAiOpen(true);
    setAiData(null);
    setAiError(null);
    metricInsightsMutation.mutate(cfg);
  }, [data, startStr, endStr, t, id, metricInsightsMutation]);

  const openOperatorMetricAi = useCallback(
    (partial) => {
      if (!data) return;
      const period = { start: startStr, end: endStr };
      const cfg = mergeOperatorMetricCfg(data, period, t, id, partial);
      setAiCfg(cfg);
      setAiOpen(true);
      setAiData(null);
      setAiError(null);
      metricInsightsMutation.mutate(cfg);
    },
    [data, startStr, endStr, t, id, metricInsightsMutation]
  );

  const kpiLoading = isLoading && !data;

  React.useEffect(() => {
    const lng = (i18n.language || "pt-BR").toLowerCase();
    dayjs.locale(lng.startsWith("pt") ? "pt-br" : "en");
  }, [i18n.language]);

  const user = data?.user;
  const operatorUserIdNum = Number(id);
  const socketTabs =
    Number.isFinite(operatorUserIdNum) && operatorUserIdNum > 0
      ? tabsByUserId.get(operatorUserIdNum) || 0
      : 0;
  const presenceOperator = useMemo(
    () => ({
      data_logout: user?.dataLogout ?? null,
      data_ultimo_acesso: user?.ultimoAcesso ?? null,
    }),
    [user?.dataLogout, user?.ultimoAcesso]
  );
  const presenceKind = useMemo(() => {
    if (!user) return "offline";
    return getTeamPresenceKind(presenceOperator, socketTabs);
  }, [user, presenceOperator, socketTabs]);

  const presenceTooltip = useCallback(
    (kind, tabs) => {
      const tip =
        kind === "online"
          ? t("dashboardPage.live.onlinePresenceTooltip")
          : kind === "away"
            ? t("dashboardPage.live.awayPresenceTooltip")
            : t("dashboardPage.live.offlinePresenceTooltip");
      if (kind === "online" && tabs > 0) {
        return `${tip}\n${t("dashboardPage.live.openTabsCount", { count: tabs })}`;
      }
      return tip;
    },
    [t]
  );

  const presenceDotBg = useCallback(
    (kind) => {
      if (kind === "online") return theme.palette.success.main;
      if (kind === "away") return theme.palette.warning.main;
      return theme.palette.error.main;
    },
    [theme]
  );

  const presenceLabel =
    presenceKind === "online"
      ? t("dashboardPage.live.onlineNow")
      : presenceKind === "away"
        ? t("dashboardPage.live.away")
        : t("dashboardPage.live.presenceOffline");
  const PresenceStatusIcon =
    presenceKind === "online" ? WifiIcon : presenceKind === "away" ? AccessTimeIcon : PowerSettingsNewIcon;

  const cards = data?.cards || {};
  const charts = data?.charts || {};
  const maxHourVal = useMemo(() => {
    const arr = data?.interactionByHour || [];
    return Math.max(1, ...arr.map((x) => Number(x.value) || 0));
  }, [data?.interactionByHour]);

  const errMsg =
    error &&
    (typeof error?.message === "string"
      ? error.message
      : t("userOperatorAnalytics.loadError"));

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language?.startsWith("pt") ? "pt-br" : "en"}>
      <Box sx={{ pt: 1, pb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate("/settings/users")} size="small">
            {t("userOperatorAnalytics.back")}
          </Button>
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ position: "relative", flexShrink: 0 }}>
              <Avatar src={undefined} sx={{ width: 44, height: 44, bgcolor: "grey.200", color: "text.primary" }}>
                {String(user?.nome || "?")
                  .trim()
                  .split(/\s+/)
                  .slice(0, 2)
                  .map((p) => p[0]?.toUpperCase())
                  .join("") || "?"}
              </Avatar>
              {user ? (
                <Tooltip title={presenceTooltip(presenceKind, socketTabs)} placement="right" arrow>
                  <Box
                    component="span"
                    sx={{
                      position: "absolute",
                      right: 2,
                      bottom: 2,
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: presenceDotBg(presenceKind),
                      border: "2px solid",
                      borderColor: "background.paper",
                      cursor: "help",
                    }}
                  />
                </Tooltip>
              ) : null}
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                {user?.nome || (isLoading ? "…" : "—")}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  columnGap: 0.5,
                  rowGap: 0.25,
                }}
              >
                {user?.email ? (
                  <Typography variant="body2" color="text.secondary" component="span">
                    {user.email}
                  </Typography>
                ) : null}
                {user?.cargoNome ? (
                  <>
                    {user?.email ? (
                      <Typography variant="body2" color="text.secondary" component="span">
                        ·
                      </Typography>
                    ) : null}
                    <Stack direction="row" alignItems="center" spacing={0.25} component="span">
                      <Typography variant="body2" color="text.secondary" component="span">
                        {user.cargoNome}
                      </Typography>
                      <Tooltip title={tChat("analytics.explainAi")}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={openAi}
                            disabled={!data || metricInsightsMutation.isLoading}
                            aria-label={tChat("analytics.explainAiAria")}
                            sx={{ p: 0.25 }}
                          >
                            <AutoAwesome sx={{ fontSize: 18 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </>
                ) : null}
              </Box>
              <Stack direction="row" spacing={0.75} sx={{ mt: 0.5 }} alignItems="center" flexWrap="wrap">
                {user ? (
                  <>
                    <Tooltip title={presenceTooltip(presenceKind, socketTabs)}>
                      <Chip
                        size="small"
                        icon={<PresenceStatusIcon sx={{ "&&": { fontSize: 16 } }} />}
                        label={presenceLabel}
                        color={presenceKind === "online" ? "success" : presenceKind === "away" ? "warning" : "error"}
                        variant="outlined"
                        sx={{ fontWeight: 600, cursor: "help" }}
                      />
                    </Tooltip>
                    {presenceKind === "online" && socketTabs > 0 ? (
                      <Chip
                        size="small"
                        icon={<TabIcon sx={{ "&&": { fontSize: 14 } }} />}
                        label={t("dashboardPage.live.openTabsCount", { count: socketTabs })}
                        variant="filled"
                        sx={{ fontWeight: 600 }}
                      />
                    ) : null}
                  </>
                ) : null}
                {user?.ultimoAcesso ? (
                  <Typography variant="caption" color="text.secondary">
                    {t("userOperatorAnalytics.lastAccess")}: {formatDateTimePtBR(user.ultimoAcesso)}
                  </Typography>
                ) : null}
              </Stack>
            </Box>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", sm: "center" }}
            flexWrap={{ sm: "wrap" }}
            useFlexGap
          >
            <DatePicker
              label={t("userOperatorAnalytics.start")}
              value={start}
              onChange={(v) => v && setStart(v.startOf("day"))}
              slotProps={{ textField: { size: "small" } }}
            />
            <DatePicker
              label={t("userOperatorAnalytics.end")}
              value={end}
              onChange={(v) => v && setEnd(v.endOf("day"))}
              slotProps={{ textField: { size: "small" } }}
            />
            <Button variant="outlined" size="small" onClick={() => refetch()} disabled={isFetching}>
              {isFetching ? <CircularProgress size={16} /> : t("userOperatorAnalytics.apply")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<AutoAwesome fontSize="small" />}
              onClick={openAi}
              disabled={!data || metricInsightsMutation.isLoading}
              sx={{
                flexShrink: 0,
                alignSelf: { xs: "stretch", sm: "auto" },
                whiteSpace: "nowrap",
                px: 1.5,
              }}
            >
              {t("userOperatorAnalytics.aiButton")}
            </Button>
          </Stack>
        </Stack>

        <Alert severity="info" sx={{ mb: 2 }} variant="outlined">
          {t("userOperatorAnalytics.scopeNote")}
        </Alert>

        {errMsg ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errMsg}
          </Alert>
        ) : null}

        {(isLoading && !data) || isFetching ? <LinearProgress sx={{ mb: 2 }} /> : null}

        {!isLoading && !data && !error ? (
          <Typography color="text.secondary">{t("userOperatorAnalytics.noData")}</Typography>
        ) : null}

        {data ? (
          <Grid container spacing={2}>
            <OperatorKpiCard
              loading={kpiLoading}
              aiBusy={metricInsightsMutation.isLoading}
              icon={<SupportAgentIcon />}
              title={t("userOperatorAnalytics.kpiTotal")}
              value={BR(cards.totalAtendimentos ?? 0)}
              color={SERIES[0]}
              sub={tChat("analytics.selectedPeriod")}
              explainAi={tChat("analytics.explainAi")}
              explainAiAria={tChat("analytics.explainAiAria")}
              onAi={() =>
                openOperatorMetricAi({
                  metric: "total_solicitacoes",
                  metricName: t("userOperatorAnalytics.kpiTotal"),
                  metricLabel: t("userOperatorAnalytics.kpiTotal"),
                  metricDefinition:
                    "Quantidade de solicitações em que este usuário está como responsável no período (mesmo escopo do painel de atendimento).",
                  metricValue: cards.totalAtendimentos ?? 0,
                  metricHuman: BR(cards.totalAtendimentos ?? 0),
                })
              }
            />
            <OperatorKpiCard
              loading={kpiLoading}
              aiBusy={metricInsightsMutation.isLoading}
              icon={<AccessTimeIcon />}
              title={tChat("analytics.ahtAvg")}
              value={secToHuman(cards.avgTMAsec)}
              color={SERIES[1]}
              sub={tChat("analytics.ahtHint")}
              explainAi={tChat("analytics.explainAi")}
              explainAiAria={tChat("analytics.explainAiAria")}
              onAi={() =>
                openOperatorMetricAi({
                  metric: "tma",
                  metricName: "TMA (Tempo médio de atendimento)",
                  metricLabel: tChat("analytics.ahtHint"),
                  metricDefinition:
                    "Tempo médio entre a abertura da solicitação e sua finalização, para solicitações sob responsabilidade deste atendente.",
                  metricSec: cards.avgTMAsec,
                  metricHuman: secToHuman(cards.avgTMAsec),
                  metricValue: cards.avgTMAsec,
                })
              }
            />
            <OperatorKpiCard
              loading={kpiLoading}
              aiBusy={metricInsightsMutation.isLoading}
              icon={<TimerIcon />}
              title={tChat("analytics.awtAvg")}
              value={secToHuman(cards.avgTMEsec)}
              color={SERIES[2]}
              sub={tChat("analytics.awtHint")}
              explainAi={tChat("analytics.explainAi")}
              explainAiAria={tChat("analytics.explainAiAria")}
              onAi={() =>
                openOperatorMetricAi({
                  metric: "tme",
                  metricName: "TME (Tempo médio de espera)",
                  metricLabel: tChat("analytics.awtHint"),
                  metricDefinition:
                    "Tempo médio de espera do cliente até o primeiro atendimento humano, nas solicitações sob responsabilidade deste atendente.",
                  metricSec: cards.avgTMEsec,
                  metricHuman: secToHuman(cards.avgTMEsec),
                  metricValue: cards.avgTMEsec,
                })
              }
            />
            <OperatorKpiCard
              loading={kpiLoading}
              aiBusy={metricInsightsMutation.isLoading}
              icon={<GroupsIcon />}
              title={tChat("analytics.peopleContacted")}
              value={BR(data.operatorDistinctClients ?? 0)}
              color={SERIES[3]}
              sub={tChat("analytics.peopleContactedHint")}
              explainAi={tChat("analytics.explainAi")}
              explainAiAria={tChat("analytics.explainAiAria")}
              onAi={() =>
                openOperatorMetricAi({
                  metric: "operator_clientes_distintos",
                  metricName: tChat("analytics.peopleContacted"),
                  metricLabel: tChat("analytics.peopleContacted"),
                  metricDefinition: t("userOperatorAnalytics.kpiPeopleContactedAiDefinition"),
                  metricValue: data.operatorDistinctClients ?? 0,
                  metricHuman: BR(data.operatorDistinctClients ?? 0),
                })
              }
            />
            <OperatorKpiCard
              loading={kpiLoading}
              aiBusy={metricInsightsMutation.isLoading}
              icon={<SendIcon />}
              title={t("userOperatorAnalytics.messagesSent")}
              value={BR(data.operatorMessagesTotal ?? 0)}
              color={SERIES[4]}
              sub={tChat("analytics.selectedPeriod")}
              explainAi={tChat("analytics.explainAi")}
              explainAiAria={tChat("analytics.explainAiAria")}
              onAi={() =>
                openOperatorMetricAi({
                  metric: "operator_mensagens_enviadas",
                  metricName: t("userOperatorAnalytics.messagesSent"),
                  metricLabel: t("userOperatorAnalytics.messagesSent"),
                  metricDefinition:
                    "Total de mensagens enviadas por este atendente nas conversas das solicitações sob sua responsabilidade no período.",
                  metricValue: data.operatorMessagesTotal ?? 0,
                  metricHuman: BR(data.operatorMessagesTotal ?? 0),
                })
              }
            />

            <Grid item xs={12} md={6}>
              <Card elevation={1} sx={{ height: "100%", ...dashboardSectionCardSx }}>
                <CardContent sx={{ pt: 2, pb: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <InsightsIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
                      {t("userOperatorAnalytics.typesTop")}
                    </Typography>
                    <Tooltip title={tChat("analytics.explainAi")}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() =>
                            openOperatorMetricAi({
                              metric: "motivos_top_paths",
                              metricName: t("userOperatorAnalytics.typesTop"),
                              metricLabel: t("userOperatorAnalytics.typesTop"),
                              metricDefinition:
                                "Principais combinações de tipo e subtipos nas solicitações sob responsabilidade deste atendente no período.",
                              metricValue: charts.motivosTop || [],
                              metricHuman: "",
                            })
                          }
                          disabled={metricInsightsMutation.isLoading}
                          aria-label={tChat("analytics.explainAiAria")}
                        >
                          <AutoAwesome fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                  <TableContainer
                    variant="outlined"
                    sx={{ bgcolor: "background.paper", borderRadius: 1, maxHeight: 360 }}
                  >
                    <Table size="small" stickyHeader sx={dataTableSx}>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t("userOperatorAnalytics.colPath")}</TableCell>
                          <TableCell align="right" sx={{ width: 88 }}>
                            {t("userOperatorAnalytics.colQty")}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(charts.motivosTop || []).slice(0, 15).length ? (
                          (charts.motivosTop || []).slice(0, 15).map((row, idx) => (
                            <TableRow key={idx} hover>
                              <TableCell>{row.path}</TableCell>
                              <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                                {row.value}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={2} align="center" sx={{ py: 3, color: "text.secondary" }}>
                              <Typography variant="body2">{t("userOperatorAnalytics.emptyTable")}</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card elevation={1} sx={{ height: "100%", ...dashboardSectionCardSx }}>
                <CardContent sx={{ pt: 2, pb: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
                      {t("userOperatorAnalytics.channels")}
                    </Typography>
                    <Tooltip title={tChat("analytics.explainAi")}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() =>
                            openOperatorMetricAi({
                              metric: "operator_canais_origem",
                              metricName: t("userOperatorAnalytics.channels"),
                              metricLabel: t("userOperatorAnalytics.channels"),
                              metricDefinition:
                                "Distribuição por canal de origem das solicitações atribuídas a este atendente no período.",
                              metricValue: data.channels || [],
                              metricHuman: "",
                            })
                          }
                          disabled={metricInsightsMutation.isLoading}
                          aria-label={tChat("analytics.explainAiAria")}
                        >
                          <AutoAwesome fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                  <TableContainer
                    variant="outlined"
                    sx={{ bgcolor: "background.paper", borderRadius: 1, maxHeight: 360 }}
                  >
                    <Table size="small" stickyHeader sx={dataTableSx}>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t("userOperatorAnalytics.colChannel")}</TableCell>
                          <TableCell align="right" sx={{ width: 88 }}>
                            {t("userOperatorAnalytics.colQty")}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(data.channels || []).length ? (
                          (data.channels || []).map((row, idx) => (
                            <TableRow key={idx} hover>
                              <TableCell>{row.name}</TableCell>
                              <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                                {row.value}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={2} align="center" sx={{ py: 3, color: "text.secondary" }}>
                              <Typography variant="body2">{t("userOperatorAnalytics.emptyTable")}</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card elevation={1} sx={dashboardSectionCardSx}>
                <CardContent sx={{ pt: 2, pb: 2 }}>
                  <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 0.5 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {t("userOperatorAnalytics.interactionHours")}
                      </Typography>
                    </Box>
                    <Tooltip title={tChat("analytics.explainAi")}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() =>
                            openOperatorMetricAi({
                              metric: "operator_interaction_hours",
                              metricName: t("userOperatorAnalytics.interactionHours"),
                              metricLabel: t("userOperatorAnalytics.interactionHours"),
                              metricDefinition:
                                "Volume de mensagens enviadas por este atendente por hora do servidor, nas solicitações sob sua responsabilidade.",
                              metricValue: (data.interactionByHour || []).map((h) => ({
                                hour: h.hour,
                                value: h.value,
                              })),
                              metricHuman: "",
                            })
                          }
                          disabled={metricInsightsMutation.isLoading}
                          aria-label={tChat("analytics.explainAiAria")}
                          sx={{ mt: -0.25 }}
                        >
                          <AutoAwesome fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    {t("userOperatorAnalytics.interactionHoursHint")}
                  </Typography>
                  <TableContainer
                    variant="outlined"
                    sx={{ bgcolor: "background.paper", borderRadius: 1, maxHeight: 360 }}
                  >
                    <Table size="small" stickyHeader sx={dataTableSx}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: 64 }}>{t("userOperatorAnalytics.colHour")}</TableCell>
                          <TableCell>{t("userOperatorAnalytics.colDistribution")}</TableCell>
                          <TableCell align="right" sx={{ width: 72 }}>
                            {t("userOperatorAnalytics.colQty")}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(data.interactionByHour || []).length ? (
                          (data.interactionByHour || []).map((row) => (
                            <TableRow key={row.hour} hover>
                              <TableCell sx={{ fontVariantNumeric: "tabular-nums" }}>{hourLabel(row.hour)}</TableCell>
                              <TableCell sx={{ py: 1.25 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={Math.min(100, (Number(row.value) / maxHourVal) * 100)}
                                  sx={{
                                    height: 10,
                                    borderRadius: 1,
                                    bgcolor: (th) => alpha(th.palette.primary.main, 0.08),
                                    "& .MuiLinearProgress-bar": { borderRadius: 1 },
                                  }}
                                />
                              </TableCell>
                              <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                                {row.value}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={3} align="center" sx={{ py: 3, color: "text.secondary" }}>
                              <Typography variant="body2">{t("userOperatorAnalytics.emptyHour")}</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card elevation={1} sx={dashboardSectionCardSx}>
                <CardContent sx={{ pt: 2, pb: 2 }}>
                  <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 0.5 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {t("userOperatorAnalytics.surveys")}
                      </Typography>
                    </Box>
                    <Tooltip title={tChat("analytics.explainAi")}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() =>
                            openOperatorMetricAi({
                              metric: "operator_surveys_resumo",
                              metricName: t("userOperatorAnalytics.surveys"),
                              metricLabel: t("userOperatorAnalytics.surveys"),
                              metricDefinition:
                                "Pesquisas respondidas por clientes em solicitações sob responsabilidade deste atendente: concluídas, nota média e tempo médio.",
                              metricValue: data.surveys?.bySurvey || [],
                              metricHuman: "",
                            })
                          }
                          disabled={metricInsightsMutation.isLoading}
                          aria-label={tChat("analytics.explainAiAria")}
                          sx={{ mt: -0.25 }}
                        >
                          <AutoAwesome fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    {t("userOperatorAnalytics.surveysHint")}
                  </Typography>
                  <TableContainer
                    variant="outlined"
                    sx={{ bgcolor: "background.paper", borderRadius: 1, maxHeight: 360 }}
                  >
                    <Table size="small" stickyHeader sx={dataTableSx}>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t("userOperatorAnalytics.colSurvey")}</TableCell>
                          <TableCell align="right" sx={{ width: 100 }}>
                            {t("userOperatorAnalytics.colCompleted")}
                          </TableCell>
                          <TableCell align="right" sx={{ width: 100 }}>
                            {t("userOperatorAnalytics.colAvgScore")}
                          </TableCell>
                          <TableCell align="right" sx={{ width: 112 }}>
                            {t("userOperatorAnalytics.colAvgTime")}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(data.surveys?.bySurvey || []).length ? (
                          (data.surveys?.bySurvey || []).map((row, idx) => (
                            <TableRow key={idx} hover>
                              <TableCell>{row.surveyTitle}</TableCell>
                              <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                                {row.completed}
                              </TableCell>
                              <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                                {row.avgScore != null ? row.avgScore : "—"}
                              </TableCell>
                              <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                                {row.avgTimeToCompleteSec != null ? fmtDurationSec(row.avgTimeToCompleteSec) : "—"}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={4} align="center" sx={{ py: 3, color: "text.secondary" }}>
                              <Typography variant="body2">{t("userOperatorAnalytics.emptySurveys")}</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ) : null}

        <MetricAiInsightsDialog
          open={aiOpen}
          metric={aiCfg?.metric}
          metricName={aiCfg?.metricName}
          metricLabel={aiCfg?.metricLabel}
          metricHuman={aiCfg?.metricHuman}
          period={aiCfg?.period}
          loading={metricInsightsMutation.isLoading}
          error={aiError}
          data={aiData}
          scope="atendimento"
          basePayload={aiCfg || {}}
          onClose={() => setAiOpen(false)}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default UserOperatorAnalytics;
